<template>
  <div>
    <wa-dropdown ref="dropdown" placement="bottom-start" distance="2" @wa-after-show="focusInput">
      <wa-button
        variant="neutral"
        appearance="outlined"
        slot="trigger"
        class="employeeSwitch"
        with-caret
      >
        {{ deviceStore.settings.warehouse }}
      </wa-button>
      <wa-input
        ref="searchInput"
        v-model="query"
        :placeholder="$t('deviceSettings.searchMarket')"
        size="m"
        @keydown="handleSearchKeydown"
      >
        <wa-icon library="bootstrap" slot="start" name="search"></wa-icon>
      </wa-input>
      <wa-dropdown-item style="pointer-events: none">
        <div class="listItem" style="line-height: 1.36">
          <b>{{ deviceStore.settings.currentEmployee.name }}</b
          ><br />
          <div class="text-color-neutral">{{ deviceStore.settings.currentEmployee.email }}</div>
        </div>
      </wa-dropdown-item>
      <wa-divider></wa-divider>
      <template v-if="query.length > 0 && marketsList.length > 0">
        <wa-dropdown-item v-for="item in marketsList" :key="item.id" @click.stop="setMarket(item)">
          <div class="storeItem">
            <div>{{ item.code }}</div>
            <div>{{ item.name }}</div>
          </div>
        </wa-dropdown-item>
      </template>
      <template v-else-if="lastMarkets.length > 0 && query.length === 0">
        <wa-dropdown-item
          v-for="(item, index) in lastMarkets"
          :key="index"
          @click.stop="setMarket(item)"
        >
          <div class="storeItem">
            <div>{{ item.code }}</div>
            <div>{{ item?.name }}</div>
          </div>
        </wa-dropdown-item>
      </template>
      <div v-else-if="noRes" class="noResults">{{ $t('deviceSettings.noMarketFound') }}</div>
      <div v-else class="noResults">{{ $t('deviceSettings.enterSearchTerm') }}</div>
    </wa-dropdown>
    <wa-dialog light-dismiss :open="loading" without-header no-footer>
      <div class="loaderLayout">
        <wa-spinner style="font-size: 2rem; --track-width: 3px"></wa-spinner
        >{{ $t('deviceSettings.pleaseWait') }}
      </div>
    </wa-dialog>
  </div>
</template>

<script setup>
import '@awesome.me/webawesome/dist/components/button/button.js'
import '@awesome.me/webawesome/dist/components/dropdown/dropdown.js'
import '@awesome.me/webawesome/dist/components/dropdown-item/dropdown-item.js'
import '@awesome.me/webawesome/dist/components/divider/divider.js'
import '@awesome.me/webawesome/dist/components/spinner/spinner.js'
import '@awesome.me/webawesome/dist/components/input/input.js'

import { ref, onMounted } from 'vue'
import { useDeviceStore } from '@/stores/device'
import { useCartStore } from '@/stores/cart'
import { useCustomerStore } from '@/stores/customer'
import { useAppointmentsStore } from '@/stores/appointments'
import { useMarket } from '@/composables/useMarket.js'
import { watchDebounced } from '@vueuse/core'
import { hideElement } from '@/utils/webawesome.js'

const dropdown = ref()
const searchInput = ref()
const deviceStore = useDeviceStore()
const cartStore = useCartStore()
const customerStore = useCustomerStore()
const appointmentsStore = useAppointmentsStore()
const { getMarketsList, getMarketSettingsFromWarehouse } = useMarket()
const query = ref('')
const marketsList = ref([])
const lastMarkets = ref([])
const loading = ref(false)
const noRes = ref(false)

defineEmits(['closeLoginDialog'])

onMounted(() => {
  // Load last markets from localStorage
  const storedMarkets = localStorage.getItem('lastMarkets')
  if (storedMarkets) {
    try {
      lastMarkets.value = JSON.parse(storedMarkets)
    } catch (e) {
      console.error('Error parsing lastMarkets from localStorage:', e)
      lastMarkets.value = []
    }
  }
})

watchDebounced(
  () => query.value,
  async (newVal, oldVal) => {
    if (newVal !== oldVal && newVal.length > 1) {
      await listMarkets(query.value)
    }

    if (newVal.length === 0) {
      resetMarketList()
    }
  },
  { debounce: 400, maxWait: 2000 }
)

async function listMarkets(query) {
  if (query === '') {
    marketsList.value = []
    return
  }

  try {
    marketsList.value = await getMarketsList('name_or_code_i_cont_any', query)
    if (marketsList.value.length === 0) {
      noRes.value = true
    } else {
      noRes.value = false
    }
  } catch (e) {
    marketsList.value = []
    throw new Error(e)
  }
}

// Web Awesome moves focus to the first dropdown item once the menu has opened; the user wants to
// type into the search input, so hand focus straight back.
function focusInput() {
  searchInput.value?.focus()
}

// While the dropdown is open, Web Awesome listens for keydown on `document` and uses printable
// keys for type-ahead (focusing the matching item), which would swallow the characters typed
// into the search input. Let only the keys the dropdown needs for closing/navigation through.
const DROPDOWN_NAVIGATION_KEYS = ['Escape', 'ArrowDown', 'ArrowUp']

function handleSearchKeydown(event) {
  if (!DROPDOWN_NAVIGATION_KEYS.includes(event.key)) {
    event.stopPropagation()
  }
}

function resetMarketList() {
  marketsList.value = []
}

/**
 * Update the lastMarkets array and save it to localStorage.
 * @param {Object} market - The market object to add to the lastMarkets array
 */
function updateLastMarkets(market) {
  const marketEntry = {
    code: market.code,
    name: market.name,
    id: market.id
  }

  const filteredMarkets = lastMarkets.value.filter((item) => item.code !== market.code)
  filteredMarkets.unshift(marketEntry)
  lastMarkets.value = filteredMarkets.slice(0, 5)
  localStorage.setItem('lastMarkets', JSON.stringify(lastMarkets.value))
}

/**
 * Sets the market for the user. This is used when the user selects a market
 * from the dropdown. It updates the last markets history and sets the
 * deviceStore.settings with the new settings.
 *
 * @param {Object} warehouse - The warehouse object selected by the user
 * @returns {Promise<void>}
 */
async function setMarket(warehouse) {
  // We hard-reload the page below to apply the new store. Flag it first so the appointments
  // store skips the refresh that the settings change would otherwise trigger — the reload
  // would only abort those fetches (NetworkError / Failed to fetch).
  appointmentsStore.reloading = true
  cartStore.$reset()
  customerStore.reset()
  loading.value = true
  hideElement(dropdown.value)
  query.value = ''
  marketsList.value = []

  updateLastMarkets(warehouse)

  const settings = await getMarketSettingsFromWarehouse(warehouse.name)
  settings.deviceType = 'Personal'
  settings.deviceId = deviceStore.user.email

  deviceStore.$patch({
    settings: {
      ...deviceStore.settings,
      ...settings
    }
  })
  localStorage.setItem('marketSettings', JSON.stringify(settings))

  setTimeout(() => {
    window.location.reload()
  }, 1000)
}
</script>

<style lang="postcss" scoped>
wa-dropdown::part(menu) {
  padding-top: 0;
  padding-bottom: 0;
}

wa-divider {
  margin-top: 0;
}

wa-input {
  &::part(base) {
    border-radius: 0;
    border-width: 0 0 1px 0;
    font-size: 14px;
  }

  .input__prefix ::slotted(*) {
    margin-left: 30px;
  }
}

wa-dropdown-item {
  padding: var(--wa-space-xs) var(--wa-space-s);

  &::part(checkmark),
  &::part(submenu-icon) {
    display: none;
  }
}

.menu-label {
  &::part(base) {
    font-size: var(--wa-font-size-s);
    color: var(--wa-color-neutral-50);
    padding: var(--wa-space-xs) var(--wa-space-s);
  }
}

.storeItem {
  display: flex;
  gap: var(--wa-space-m);

  div:nth-child(1) {
    width: 40px;
  }
}

.noResults {
  padding: var(--wa-space-m);
  text-align: center;
  color: var(--wa-color-neutral-50);
  font-size: var(--wa-font-size-s);
  display: flex;
  align-items: center;
  justify-content: center;
}

.loaderLayout {
  display: flex;
  flex-direction: column;
  gap: var(--wa-space-m);
  justify-content: center;
  align-items: center;
  height: 100px;
  padding-top: var(--wa-space-l);
}
</style>
